import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Mic, Square, RotateCcw, Settings, Trash2, Copy, ThumbsUp, ThumbsDown, MoreVertical } from 'lucide-react';
import { useSelector } from 'react-redux';

const ChatPage = () => {
 const [messages, setMessages] = useState([
 {
 id: 1,
 type: 'assistant',
 content: '你好！我是你的AI助手，有什么可以帮助你的吗？',
 timestamp: new Date(),
 }
 ]);
 const [inputMessage, setInputMessage] = useState('');
 const [isTyping, setIsTyping] = useState(false);
 const [isRecording, setIsRecording] = useState(false);
 const messagesEndRef = useRef(null);
 const inputRef = useRef(null);
 
 // 获取用户信息
 const { user, isLoggedIn } = useSelector((state) => state.user);

 // 自动滚动到底部
 const scrollToBottom = () => {
 messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
 };

 useEffect(() => {
 scrollToBottom();
 }, [messages]);

 // 发送消息
 const handleSendMessage = async () => {
 if (!inputMessage.trim()) return;

 const userMessage = {
 id: Date.now(),
 type: 'user',
 content: inputMessage.trim(),
 timestamp: new Date(),
 };

 setMessages(prev => [...prev, userMessage]);
 setInputMessage('');
 setIsTyping(true);

 // 模拟AI响应
 setTimeout(() => {
 const aiResponse = {
 id: Date.now() + 1,
 type: 'assistant',
 content: '这是一个模拟的AI回复。在实际应用中，这里会连接到真正的AI模型来生成回复。',
 timestamp: new Date(),
 };
 setMessages(prev => [...prev, aiResponse]);
 setIsTyping(false);
 }, 1000 + Math.random() * 2000);
 };

 // 按键处理
 const handleKeyPress = (e) => {
 if (e.key === 'Enter' && !e.shiftKey) {
 e.preventDefault();
 handleSendMessage();
 }
 };

 // 清空对话
 const handleClearChat = () => {
 setMessages([
 {
 id: 1,
 type: 'assistant',
 content: '对话已清空。有什么新的问题吗？',
 timestamp: new Date(),
 }
 ]);
 };

 // 复制消息
 const handleCopyMessage = (content) => {
 navigator.clipboard.writeText(content);
 // 这里可以添加复制成功的提示
 };

 // 格式化时间
 const formatTime = (timestamp) => {
 return new Intl.DateTimeFormat('zh-CN', {
 hour: '2-digit',
 minute: '2-digit',
 }).format(timestamp);
 };

 return (
 <div className="min-h-screen bg-cream-100 flex flex-col">
 {/* 顶部栏 */}
 <div className="bg-cream-50 border-b border-ink-200 px-4 py-3 shadow-sm">
 <div className="max-w-4xl mx-auto flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center">
 <span className="text-white font-bold text-lg">AI</span>
 </div>
 <div>
 <h1 className="text-lg font-semibold text-ink-900">AI智能助手</h1>
 <p className="text-sm text-ink-500">
 {isLoggedIn ? `欢迎回来，${user?.name || user?.email || '用户'}` : '请登录以获得更好的体验'}
 </p>
 </div>
 </div>
 
 <div className="flex items-center gap-2">
 <button
 onClick={handleClearChat}
 className="p-2 hover:bg-cream-100 rounded-lg transition-colors duration-200"
 title="清空对话"
 >
 <Trash2 className="w-5 h-5 text-ink-600" />
 </button>
 <button className="p-2 hover:bg-cream-100 rounded-lg transition-colors duration-200" title="设置">
 <Settings className="w-5 h-5 text-ink-600" />
 </button>
 </div>
 </div>
 </div>

 {/* 消息区域 */}
 <div className="flex-1 overflow-hidden">
 <div className="max-w-4xl mx-auto h-full flex flex-col">
 <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
 {messages.map((message) => (
 <div
 key={message.id}
 className={`flex gap-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
 >
 {message.type === 'assistant' && (
 <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
 <span className="text-white font-bold text-sm">AI</span>
 </div>
 )}
 
 <div className={`max-w-3xl ${message.type === 'user' ? 'order-first' : ''}`}>
 <div
 className={`px-4 py-3 rounded-2xl ${
 message.type === 'user'
 ? 'bg-accent-500 text-white ml-auto'
 : 'bg-cream-50 border border-ink-200 shadow-sm'
 }`}
 >
 <p className="text-sm leading-relaxed whitespace-pre-wrap">
 {message.content}
 </p>
 </div>
 
 <div className={`flex items-center gap-2 mt-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
 <span className="text-xs text-ink-500">
 {formatTime(message.timestamp)}
 </span>
 
 {message.type === 'assistant' && (
 <div className="flex items-center gap-1">
 <button
 onClick={() => handleCopyMessage(message.content)}
 className="p-1 hover:bg-cream-100 rounded transition-colors duration-200"
 title="复制"
 >
 <Copy className="w-3 h-3 text-ink-400" />
 </button>
 <button className="p-1 hover:bg-cream-100 rounded transition-colors duration-200" title="点赞">
 <ThumbsUp className="w-3 h-3 text-ink-400" />
 </button>
 <button className="p-1 hover:bg-cream-100 rounded transition-colors duration-200" title="点踩">
 <ThumbsDown className="w-3 h-3 text-ink-400" />
 </button>
 </div>
 )}
 </div>
 </div>

 {message.type === 'user' && (
 <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
 <span className="text-white font-bold text-sm">
 {isLoggedIn && user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
 </span>
 </div>
 )}
 </div>
 ))}

 {/* AI正在输入指示器 */}
 {isTyping && (
 <div className="flex gap-4 justify-start">
 <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
 <span className="text-white font-bold text-sm">AI</span>
 </div>
 <div className="bg-cream-50 border border-ink-200 shadow-sm px-4 py-3 rounded-2xl">
 <div className="flex items-center gap-1">
 <div className="w-2 h-2 bg-ink-300 rounded-full animate-bounce"></div>
 <div className="w-2 h-2 bg-ink-300 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
 <div className="w-2 h-2 bg-ink-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
 </div>
 </div>
 </div>
 )}

 <div ref={messagesEndRef} />
 </div>
 </div>
 </div>

 {/* 输入区域 */}
 <div className="bg-cream-50 border-t border-ink-200 px-4 py-4">
 <div className="max-w-4xl mx-auto">
 <div className="flex items-end gap-3">
 {/* 附件按钮 */}
 <button className="p-2 hover:bg-cream-100 rounded-lg transition-colors duration-200 mb-1">
 <Paperclip className="w-5 h-5 text-ink-600" />
 </button>

 {/* 输入框 */}
 <div className="flex-1 relative">
 <textarea
 ref={inputRef}
 value={inputMessage}
 onChange={(e) => setInputMessage(e.target.value)}
 onKeyPress={handleKeyPress}
 placeholder="输入消息... (Shift+Enter 换行)"
 className="w-full px-4 py-3 border border-ink-300 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-200"
 rows="1"
 style={{
 minHeight: '48px',
 maxHeight: '120px',
 overflowY: inputMessage.length > 100 ? 'auto' : 'hidden'
 }}
 />
 </div>

 {/* 语音/发送按钮 */}
 <div className="flex items-center gap-2 mb-1">
 <button
 className={`p-2 rounded-lg transition-colors duration-200 ${
 isRecording 
 ? 'bg-red-500 text-white' 
 : 'hover:bg-cream-100 text-ink-600'
 }`}
 onClick={() => setIsRecording(!isRecording)}
 >
 {isRecording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
 </button>

 <button
 onClick={handleSendMessage}
 disabled={!inputMessage.trim() || isTyping}
 className={`p-2 rounded-lg transition-all duration-200 ${
 inputMessage.trim() && !isTyping
 ? 'bg-accent-500 text-white hover:bg-accent-600 transform'
 : 'bg-ink-200 text-ink-500 cursor-not-allowed'
 }`}
 >
 <Send className="w-5 h-5" />
 </button>
 </div>
 </div>

 {/* 快捷建议 */}
 <div className="mt-3 flex flex-wrap gap-2">
 {[
 '你好，介绍一下自己',
 '帮我写一篇文章',
 '解释一个概念',
 '翻译这段文字'
 ].map((suggestion, index) => (
 <button
 key={index}
 onClick={() => setInputMessage(suggestion)}
 className="px-3 py-1 text-sm bg-cream-100 hover:bg-ink-100 rounded-full text-ink-600 transition-colors duration-200"
 >
 {suggestion}
 </button>
 ))}
 </div>
 </div>
 </div>
 </div>
 );
};

export default ChatPage; 